import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';

interface Report {
  products: {
    locale: string;
    total: number;
    noTranslation: number;
    noSeoTitle: number;
    noSeoDescription: number;
    noDescription: number;
    noShortDescription: number;
  }[];
  images: { locale: string; images: number; noAlt: number }[];
  categories: { locale: string; total: number; noDescription: number }[];
  worst: { productId: string; name: string; missing: number }[];
}

interface Redirect {
  id: string;
  locale: string;
  oldPath: string;
  newPath: string | null;
  code: 301 | 410;
}

const LOCALES = ['lv', 'lt', 'et', 'ru', 'en'] as const;

/** Число пробелов: красным, если есть. */
function Gap({ value }: { value: number }) {
  return (
    <td className={`text-right ${value ? 'text-destructive' : 'text-muted-foreground'}`}>
      {value}
    </td>
  );
}

/** SEO: где не хватает meta, описаний и alt; редиректы со старых адресов. */
export function SeoPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const report = useQuery({
    queryKey: ['seo', 'report'],
    queryFn: () => api<Report>('/seo/report'),
  });
  const [q, setQ] = useState('');
  const redirects = useQuery({
    queryKey: ['seo', 'redirects', q],
    queryFn: () =>
      api<{ items: Redirect[] }>(`/seo/redirects${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });
  const [form, setForm] = useState({ locale: 'lv', oldPath: '', newPath: '', code: '301' });
  const [error, setError] = useState<string>();

  const save = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(undefined);
    try {
      await api('/seo/redirects', {
        method: 'POST',
        body: {
          locale: form.locale,
          oldPath: form.oldPath,
          newPath: form.code === '301' ? form.newPath : null,
          code: Number(form.code),
        },
      });
      setForm({ ...form, oldPath: '', newPath: '' });
      await queryClient.invalidateQueries({ queryKey: ['seo', 'redirects'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const remove = async (id: string) => {
    await api(`/seo/redirects/${id}`, { method: 'DELETE' });
    await queryClient.invalidateQueries({ queryKey: ['seo', 'redirects'] });
  };
  const set = (key: keyof typeof form) => (value: string) => {
    setForm({ ...form, [key]: value });
  };

  return (
    <>
      <PageHeader title={t('seo.title')} description={t('seo.hint')} />
      <div className="grid gap-6">
        <Card>
          <CardHeader
            title={t('seo.coverage')}
            description={t('seo.coverageHint')}
            actions={
              <Link to="/ai/drafts" className="text-sm text-primary hover:underline">
                {t('seo.fillWithAi')}
              </Link>
            }
          />
          <CardContent className="overflow-x-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="text-left font-normal">{t('seo.language')}</th>
                  <th className="text-right font-normal">{t('seo.products')}</th>
                  <th className="text-right font-normal">{t('seo.noTranslation')}</th>
                  <th className="text-right font-normal">{t('seo.noSeoTitle')}</th>
                  <th className="text-right font-normal">{t('seo.noSeoDescription')}</th>
                  <th className="text-right font-normal">{t('seo.noDescription')}</th>
                  <th className="text-right font-normal">{t('seo.noAlt')}</th>
                  <th className="text-right font-normal">{t('seo.categoriesNoText')}</th>
                </tr>
              </thead>
              <tbody>
                {(report.data?.products ?? []).map((p) => {
                  const img = report.data?.images.find((i) => i.locale === p.locale);
                  const cat = report.data?.categories.find((c) => c.locale === p.locale);
                  return (
                    <tr key={p.locale} className="border-t">
                      <td className="py-1.5 font-mono">{p.locale}</td>
                      <td className="text-right">{p.total}</td>
                      <Gap value={p.noTranslation} />
                      <Gap value={p.noSeoTitle} />
                      <Gap value={p.noSeoDescription} />
                      <Gap value={p.noDescription} />
                      <Gap value={img?.noAlt ?? 0} />
                      <Gap value={cat?.noDescription ?? 0} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {report.data?.worst.length ? (
              <div className="mt-4">
                <h3 className="mb-1 font-medium">{t('seo.worst')}</h3>
                <ul className="grid gap-1">
                  {report.data.worst.map((w) => (
                    <li key={w.productId}>
                      <Link
                        to="/products/$productId"
                        params={{ productId: w.productId }}
                        className="text-primary hover:underline"
                      >
                        {w.name || w.productId}
                      </Link>{' '}
                      <span className="text-muted-foreground">
                        — {t('seo.languagesMissing', { count: w.missing })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader title={t('seo.redirects')} description={t('seo.redirectsHint')} />
          <CardContent className="grid gap-4 text-sm">
            <form onSubmit={(e) => void save(e)} className="flex flex-wrap items-end gap-3">
              <Field label={t('seo.language')}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.locale}
                    onChange={(e) => {
                      set('locale')(e.target.value);
                    }}
                  >
                    {LOCALES.map((l) => (
                      <option key={l}>{l}</option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('seo.oldPath')}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.oldPath}
                    placeholder="/old-page"
                    onChange={(e) => {
                      set('oldPath')(e.target.value);
                    }}
                    className="w-56 font-mono"
                  />
                )}
              </Field>
              <Field label={t('seo.type')}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.code}
                    onChange={(e) => {
                      set('code')(e.target.value);
                    }}
                  >
                    <option value="301">{t('seo.moved')}</option>
                    <option value="410">{t('seo.gone')}</option>
                  </Select>
                )}
              </Field>
              {form.code === '301' ? (
                <Field label={t('seo.newPath')}>
                  {(id) => (
                    <Input
                      id={id}
                      value={form.newPath}
                      placeholder="/c/kitchen"
                      onChange={(e) => {
                        set('newPath')(e.target.value);
                      }}
                      className="w-56 font-mono"
                    />
                  )}
                </Field>
              ) : null}
              <Button type="submit">{t('common.save')}</Button>
            </form>
            {error ? <Alert variant="destructive">{error}</Alert> : null}
            <Input
              aria-label={t('seo.search')}
              placeholder={t('seo.search')}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
              }}
              className="max-w-xs"
            />
            <table className="w-full">
              <tbody>
                {(redirects.data?.items ?? []).map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="py-1.5 font-mono text-xs">{r.locale}</td>
                    <td className="font-mono">{r.oldPath}</td>
                    <td className="font-mono">{r.code === 301 ? `→ ${r.newPath ?? ''}` : '410'}</td>
                    <td className="text-right">
                      <Button size="sm" variant="outline" onClick={() => void remove(r.id)}>
                        {t('common.remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
